// Compression Worker Pool
// Manages a pool of Web Workers for LZ-String compression/decompression
// Falls back to main-thread if workers unavailable

import type { CompressionRequest, CompressionResponse } from '../workers/compression-worker';

type PendingJob = {
  resolve: (value: any) => void;
  reject: (reason: any) => void;
};

const POOL_SIZE = navigator.hardwareConcurrency ? Math.min(navigator.hardwareConcurrency, 4) : 2;

let pool: Worker[] = [];
let roundRobin = 0;
let jobId = 0;
const pending = new Map<string, PendingJob>();
let initialized = false;
let fallbackMode = false;

function getWorker(): Worker {
  const worker = pool[roundRobin % pool.length];
  roundRobin++;
  return worker;
}

function initPool(): void {
  if (initialized) return;
  initialized = true;

  try {
    for (let i = 0; i < POOL_SIZE; i++) {
      const worker = new Worker(
        new URL('../workers/compression-worker.ts', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (event: MessageEvent<CompressionResponse>) => {
        const { id } = event.data;
        const job = pending.get(id);
        if (!job) return;
        pending.delete(id);

        if (event.data.type === 'error') {
          job.reject(new Error(event.data.error));
        } else {
          job.resolve(event.data);
        }
      };
      worker.onerror = (err) => {
        console.error('[compression-pool] Worker error:', err);
      };
      pool.push(worker);
    }
    console.log(`[compression-pool] Initialized ${POOL_SIZE} workers`);
  } catch (err) {
    console.warn('[compression-pool] Workers unavailable, using main-thread fallback:', err);
    fallbackMode = true;
    pool = [];
  }
}

function sendToWorker(request: CompressionRequest): Promise<CompressionResponse> {
  initPool();

  if (fallbackMode || pool.length === 0) {
    return mainThreadFallback(request);
  }

  return new Promise((resolve, reject) => {
    pending.set(request.id, { resolve, reject });
    getWorker().postMessage(request);

    // Timeout safety (30s)
    setTimeout(() => {
      if (pending.has(request.id)) {
        pending.delete(request.id);
        reject(new Error('Compression worker timeout'));
      }
    }, 30000);
  });
}

async function mainThreadFallback(request: CompressionRequest): Promise<CompressionResponse> {
  const { compressToUTF16, decompressFromUTF16 } = await import('lz-string');
  const start = performance.now();

  if (request.type === 'compress') {
    const result = compressToUTF16(request.data);
    return {
      id: request.id,
      type: 'compress',
      result,
      originalSize: new Blob([request.data]).size,
      compressedSize: result.length * 2,
      durationMs: performance.now() - start,
    };
  } else {
    const result = decompressFromUTF16(request.data);
    if (result === null) {
      return { id: request.id, type: 'error', error: 'Decompression returned null' };
    }
    return {
      id: request.id,
      type: 'decompress',
      result,
      durationMs: performance.now() - start,
    };
  }
}

// ==================== Public API ====================

export interface CompressResult {
  compressed: string;
  originalSize: number;
  compressedSize: number;
  durationMs: number;
}

export async function compress(data: string): Promise<CompressResult> {
  const id = `c-${++jobId}`;
  const response = await sendToWorker({ id, type: 'compress', data });
  if (response.type === 'error') throw new Error(response.error);
  if (response.type !== 'compress') throw new Error('Unexpected response type');
  return {
    compressed: response.result,
    originalSize: response.originalSize,
    compressedSize: response.compressedSize,
    durationMs: response.durationMs,
  };
}

export async function decompress(compressed: string): Promise<string> {
  const id = `d-${++jobId}`;
  const response = await sendToWorker({ id, type: 'decompress', data: compressed });
  if (response.type === 'error') throw new Error(response.error);
  if (response.type !== 'decompress') throw new Error('Unexpected response type');
  return response.result;
}

/** Terminate all workers (for cleanup) */
export function destroyPool(): void {
  for (const w of pool) w.terminate();
  pool = [];
  pending.clear();
  initialized = false;
  fallbackMode = false;
}
